import { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { useForm, useSelector } from '@tanstack/react-form';

import { Button, InputField, SelectField, Text } from '@nvision/ui';

import { useGetCountryList, useGetStateList } from '@/views/screens/common/hooks';

import {
  type Carrier,
  type LaneSearchFormValues,
  laneSearchFormSchema,
  type PortRow,
  type PortSearchFormValues,
  type PostalSearchFormValues,
  type Postals,
} from '../../../models';
import { PortSearchModal } from '../../port';
import { PostalSearchModal } from '../../postal';
import { ProviderSearchModal } from '../../provider';

import styles from './lane-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface LaneSearchFormProps {
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: LaneSearchFormValues) => void;
  handleReset: () => void;
}

type SearchModalField = 'origin' | 'destination';

const getDefaultSearchFormValues = (): LaneSearchFormValues =>
  v.getDefaults(laneSearchFormSchema) as unknown as LaneSearchFormValues;

export const LaneSearchForm: React.FC<LaneSearchFormProps> = ({ formRef, handleSubmit, handleReset }) => {
  const { t } = useTranslation(['translation']);

  const form = useForm({
    defaultValues: getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: laneSearchFormSchema,
      onSubmit: laneSearchFormSchema,
    },
  });
  const originCountry = useSelector(form.store, (state) => state.values.originCountry);
  const destinationCountry = useSelector(form.store, (state) => state.values.destinationCountry);

  const { data: countries = [], isLoading: countriesLoading } = useGetCountryList();

  const { data: originStates = [], isLoading: originStatesLoading } = useGetStateList({
    CountryCode: originCountry?.id,
    isDisabled: false,
  });
  const { data: destinationStates = [], isLoading: destinationStatesLoading } = useGetStateList({
    CountryCode: destinationCountry?.id,
    isDisabled: false,
  });

  const [isProviderSearchOpen, setIsProviderSearchOpen] = useState(false);

  const [activePostalField, setActivePostalField] = useState<SearchModalField | null>(null);

  const [activePortField, setActivePortField] = useState<SearchModalField | null>(null);

  const [defaultValues, setDefaultValues] = useState<PostalSearchFormValues>({} as PostalSearchFormValues);
  const [postalSelections, setPostalSelections] = useState<Record<SearchModalField, Postals>>({
    destination: [],
    origin: [],
  });

  const [portDefaultValues, setPortDefaultValues] = useState<PortSearchFormValues>({} as PortSearchFormValues);

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      setPostalSelections({ destination: [], origin: [] });
      handleReset();
    },
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.validate('submit');
    form.handleSubmit();
  };

  const getDefaultValues = (field: SearchModalField) => {
    if (field === 'origin') {
      return {
        additional: {
          city: form.state.values.originCity,
          portPostal: form.state.values.originPostal,
          state: form.state.values.originState,
        },
        country: {
          id: form.state.values.originCountry?.id ?? '',
          label: form.state.values.originCountry?.label ?? '',
        },
      };
    }
    if (field === 'destination') {
      return {
        additional: {
          city: form.state.values.destinationCity,
          portPostal: form.state.values.destinationPostal,
          state: form.state.values.destinationState,
        },
        country: {
          id: form.state.values.destinationCountry?.id ?? '',
          label: form.state.values.destinationCountry?.label ?? '',
        },
      };
    }
  };

  const openFieldSearchModal = (field: SearchModalField) => {
    setDefaultValues(getDefaultValues(field) as PostalSearchFormValues);
    setActivePostalField(field);
  };

  const onPostalSelectionChange = (selection: Postals) => {
    const selected = selection[0];
    if (!selected || !activePostalField) {
      return;
    }

    const foundCountry = countries.find((country) => country.countryCode === selected.countryCode);
    if (activePostalField === 'origin') {
      form.setFieldValue('originCountry', {
        id: foundCountry?.countryCode ?? selected.countryCode ?? '',
        label: foundCountry?.countryName ?? selected.countryCode ?? '',
      });
      form.setFieldValue('originState', {
        id: selected.stateCode ?? '',
        label: selected.stateCode ?? '',
      });
      form.setFieldValue('originCity', selected.cityName);
      form.setFieldValue('originPostal', selected.postalCode);
    } else if (activePostalField === 'destination') {
      form.setFieldValue('destinationCountry', {
        id: foundCountry?.countryCode ?? selected.countryCode ?? '',
        label: foundCountry?.countryName ?? selected.countryCode ?? '',
      });
      form.setFieldValue('destinationState', {
        id: selected.stateCode ?? '',
        label: selected.stateCode ?? '',
      });
      form.setFieldValue('destinationCity', selected.cityName);
      form.setFieldValue('destinationPostal', selected.postalCode);
    }

    setPostalSelections((current) => ({ ...current, [activePostalField]: selection }));
    setActivePostalField(null);
  };

  const openPortSearchModal = (field: SearchModalField) => {
    const country = field === 'origin' ? form.state.values.originCountry : form.state.values.destinationCountry;

    setPortDefaultValues({ country: country?.id ? country : undefined } as PortSearchFormValues);
    setActivePortField(field);
  };

  const onPortSelectionChange = (port: PortRow) => {
    if (activePortField === 'origin') {
      form.setFieldValue('originPortName', port.name ?? '');
      form.setFieldValue('originPortKey', port.portKey);
    } else if (activePortField === 'destination') {
      form.setFieldValue('destinationPortName', port.name ?? '');
      form.setFieldValue('destinationPortKey', port.portKey);
    }

    setActivePortField(null);
  };

  const onProviderSelectionChange = (selection: Carrier[]) => {
    const carrier = selection[0];
    if (!carrier) {
      return;
    }

    form.setFieldValue('carrierName', carrier.carrierName);
    form.setFieldValue('carrierKey', Number(carrier.id));
    setIsProviderSearchOpen(false);
  };

  const errorMap = useSelector(form.store, (state) => state.errorMap.onSubmit);

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.locationBlock}>
        {/* Origin */}
        <div className={styles.locationColumn}>
          <Text size="sm" weight="semibold" className={styles.locationTitle}>
            {t('translation:default.fields.origin')}
          </Text>

          <form.Field name="originCountry">
            {(field) => (
              <SelectField
                label="Country"
                value={field.state.value?.id ? field.state.value : null}
                placeholder="Select"
                size="sm"
                onSelectionChange={(country) => {
                  if (!country) {
                    return;
                  }
                  field.handleChange(country);
                  form.setFieldValue('originState', undefined);
                }}
                isLoading={countriesLoading}
                onBlur={field.handleBlur}
                options={countries.map((item) => ({ id: item.countryCode, label: item.countryName }))}
                isRequired
                errorMessage={errorMap?.['originCountry.id']?.map((e) => e?.message).join('.')}
              />
            )}
          </form.Field>

          <form.Field name="originState">
            {(field) => (
              <SelectField
                label="State/Province"
                value={field.state.value?.id ? field.state.value : null}
                placeholder="Select"
                size="sm"
                onSelectionChange={(state) => state && field.handleChange(state)}
                isLoading={originStatesLoading}
                onBlur={field.handleBlur}
                options={originStates.map((item) => ({ id: item.value, label: item.label }))}
              />
            )}
          </form.Field>

          <div className={styles.cityPostalRow}>
            <form.Field name="originCity">
              {(field) => (
                <InputField
                  label="City"
                  value={field.state.value ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Enter"
                  size="sm"
                />
              )}
            </form.Field>

            <form.Field name="originPostal">
              {(field) => (
                <InputField
                  value={field.state.value ?? ''}
                  clearable
                  onChange={(e) => field.handleChange(e.target.value ? field.state.value : undefined)}
                  label="Postal"
                  placeholder="Enter"
                  size="sm"
                  onClick={() => openFieldSearchModal('origin')}
                />
              )}
            </form.Field>
          </div>

          <div className={styles.portTypeNameRow}>
            <form.Field name="originPortName">
              {(field) => (
                <InputField
                  value={field.state.value ?? ''}
                  clearable
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.handleChange(undefined);
                      form.setFieldValue('originPortKey', undefined);
                    }
                  }}
                  label="Port"
                  placeholder="Name"
                  size="sm"
                  onClick={() => openPortSearchModal('origin')}
                />
              )}
            </form.Field>
          </div>
        </div>

        {/* Destination */}
        <div className={styles.locationColumn}>
          <Text size="sm" weight="semibold" className={styles.locationTitle}>
            {t('translation:default.fields.destination')}
          </Text>

          <form.Field name="destinationCountry">
            {(field) => (
              <SelectField
                label="Country"
                value={field.state.value?.id ? field.state.value : null}
                placeholder="Select"
                size="sm"
                onSelectionChange={(country) => {
                  if (!country) {
                    return;
                  }
                  field.handleChange(country);
                  form.setFieldValue('destinationState', undefined);
                }}
                isLoading={countriesLoading}
                onBlur={field.handleBlur}
                options={countries.map((item) => ({ id: item.countryCode, label: item.countryName }))}
                isRequired
                errorMessage={errorMap?.['destinationCountry.id']?.map((e) => e?.message).join('.')}
              />
            )}
          </form.Field>

          <form.Field name="destinationState">
            {(field) => (
              <SelectField
                label="State/Province"
                value={field.state.value?.id ? field.state.value : null}
                placeholder="Select"
                size="sm"
                onSelectionChange={(state) => state && field.handleChange(state)}
                isLoading={destinationStatesLoading}
                onBlur={field.handleBlur}
                options={destinationStates.map((item) => ({ id: item.value, label: item.label }))}
              />
            )}
          </form.Field>

          <div className={styles.cityPostalRow}>
            <form.Field name="destinationCity">
              {(field) => (
                <InputField
                  label="City"
                  value={field.state.value ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Enter"
                  size="sm"
                />
              )}
            </form.Field>

            <form.Field name="destinationPostal">
              {(field) => (
                <InputField
                  value={field.state.value ?? ''}
                  clearable
                  onChange={(e) => field.handleChange(e.target.value ? field.state.value : undefined)}
                  label="Postal"
                  placeholder="Enter"
                  size="sm"
                  onClick={() => openFieldSearchModal('destination')}
                />
              )}
            </form.Field>
          </div>

          <div className={styles.portTypeNameRow}>
            <form.Field name="destinationPortName">
              {(field) => (
                <InputField
                  value={field.state.value ?? ''}
                  clearable
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.handleChange(undefined);
                      form.setFieldValue('destinationPortKey', undefined);
                    }
                  }}
                  label="Port"
                  placeholder="Name"
                  size="sm"
                  onClick={() => openPortSearchModal('destination')}
                />
              )}
            </form.Field>
          </div>
        </div>
      </div>

      <div className={styles.fieldsContainer}>
        <form.Field name="laneCode">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Lane ID"
              placeholder="Enter"
              size="sm"
              errorMessage={field.state.meta.errors.map((e) => e?.message).join('.')}
            />
          )}
        </form.Field>
        <form.Field name="carrierName">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => {
                if (!e.target.value) {
                  field.handleChange(undefined);
                  form.setFieldValue('carrierKey', undefined);
                }
              }}
              label="Transportation Provider"
              placeholder="Enter"
              size="sm"
              onClick={() => setIsProviderSearchOpen(true)}
            />
          )}
        </form.Field>
      </div>
      <Button type="submit">Search</Button>
      <Button outline secondary onClick={() => formRef.current.reset()}>
        Reset
      </Button>

      <ProviderSearchModal
        isOpen={isProviderSearchOpen}
        setIsOpen={setIsProviderSearchOpen}
        onSelectionChange={onProviderSelectionChange}
      />

      <PostalSearchModal
        defaultValues={defaultValues}
        defaultSelection={activePostalField ? postalSelections[activePostalField] : []}
        isOpen={activePostalField !== null}
        searchContext={activePostalField ?? undefined}
        setIsOpen={(isOpen) => !isOpen && setActivePostalField(null)}
        onSelectionChange={onPostalSelectionChange}
      />

      <PortSearchModal
        defaultValues={portDefaultValues}
        isOpen={activePortField !== null}
        setIsOpen={(isOpen) => !isOpen && setActivePortField(null)}
        onSelectionChange={onPortSelectionChange}
      />
    </form>
  );
};
